const SANTIAGO_TZ = "America/Santiago";

const SPANISH_SHORT_MONTHS = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

/**
 * Canonical display format for dates across the app: "18/may/2026".
 * Unambiguous (no day/month swap confusion) and short. Use this
 * everywhere a date is shown read-only.
 */
export function formatFecha(iso: string): string {
  const [y, m, d] = iso.split("-");
  const monthIdx = parseInt(m!, 10) - 1;
  const mmm = SPANISH_SHORT_MONTHS[monthIdx] ?? m;
  return `${d}/${mmm}/${y}`;
}

/**
 * Pure numeric form "DD/MM/YYYY" — kept for places where a Chilean user
 * is typing into a field, since numeric is faster on a keypad. Avoid for
 * display.
 */
export function formatFechaNumeric(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

/** "DD-MMM" (no year). Used in compact cross-references like "Retorno de vuelo del 26-may". */
export function formatFechaShort(iso: string): string {
  const [, m, d] = iso.split("-");
  const monthIdx = parseInt(m!, 10) - 1;
  const mmm = SPANISH_SHORT_MONTHS[monthIdx] ?? m;
  return `${d}-${mmm}`;
}

/**
 * Format a Chilean RUT to the canonical X.XXX.XXX-X form.
 * Accepts any common input (with or without dots/dash, upper/lower K).
 * Returns the raw value unchanged if it is too short to format, or if it
 * contains any letter other than K — which signals a foreign ID type
 * (e.g. "DNI 4.532.404") that should be stored exactly as entered.
 */
export function formatRut(raw: string): string {
  // Any letter except K/k means the caller deliberately marked this as a
  // non-Chilean ID. Leave it untouched.
  if (/[a-jl-zA-JL-Z]/.test(raw)) return raw;
  const clean = raw.replace(/[^0-9kK]/g, "").toUpperCase();
  if (clean.length < 2) return raw;
  const verifier = clean.slice(-1);
  const body = clean.slice(0, -1);
  if (body.length === 0) return raw;
  const formatted = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${formatted}-${verifier}`;
}

/** "1985-03-15" → "15-mar-1985" */
export function formatDateOfBirth(iso: string): string {
  const [y, m, d] = iso.split("-");
  const monthIdx = parseInt(m!, 10) - 1;
  const mmm = SPANISH_SHORT_MONTHS[monthIdx] ?? m;
  return `${d}-${mmm}-${y}`;
}

/** "13:00:00" -> "13:00" */
export function formatHora(h: string): string {
  return h.slice(0, 5);
}

/** "2026-05-17" -> "LUN" (3-letter Spanish weekday, uppercase, no dot). */
export function dowEs(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!, 12, 0, 0));
  return new Intl.DateTimeFormat("es-CL", {
    timeZone: "UTC",
    weekday: "short",
  })
    .format(dt)
    .replace(/\.$/, "")
    .toUpperCase();
}

/** Today's date in America/Santiago as "YYYY-MM-DD". */
export function todaySantiago(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: SANTIAGO_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/** Tomorrow's date in America/Santiago as "YYYY-MM-DD". */
export function tomorrowSantiago(): string {
  const t = new Date();
  t.setUTCDate(t.getUTCDate() + 1);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: SANTIAGO_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(t);
}
